<template>
  <div class="cfdi-related">
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          CFDI Relacionados
        </p>
      </header>

      <section class="modal-card-body">
        <AppSelectSearch
          id="cfdisRelatedType"
          name="cfdisRelatedType"
          label="Tipo de relación"
          :options="getRelationTypes()"
          :value="relationTypeSelected"
          @input="onChangeRelationType($event.target.value)"
          @change="onChangeRelationType($event.target.value)"
        />

        <AppPagination
          :current-page="currentPage"
          :buttons-max="buttonsMax"
          :pages-total="totalPages"
          :total="total"
          :size="size"
          @change="goToPage"
        />

        <div class="table-container">
          <BTable
            :data="invoices"
            :checked-rows.sync="checkedRows"
            :columns="columns"
            :loading="loading"
            :custom-is-checked="
              (checkedRows, invoices) => { return checkedRows.id === invoices.id }"
            checkable
          >
            <template slot-scope="props">
              <BTableColumn
                field="folio"
                label="Folio"
                internal
                class="invoice-column-folio"
              >
                {{ props.row.cfdi.voucherSeries }} - {{ props.row.cfdi.voucherConsecutive }}
              </BTableColumn>
              <BTableColumn
                class="invoice-column-uuid"
                field="uuid"
                label="UUID"
                internal
              >
                {{ props.row.cfdi.complement.digitalTaxStamp.uuid }}
              </BTableColumn>
              <BTableColumn
                field="total"
                label="Monto"
                internal
                class="has-text-right"
              >
                {{ props.row.cfdi.voucherTotal | toCurrency(props.row.cfdi.voucherCurrency) }}
              </BTableColumn>
              <BTableColumn
                field="moneda"
                label="Moneda"
                internal
              >
                {{ props.row.cfdi.voucherCurrency }}
              </BTableColumn>
              <BTableColumn
                field="receiverName"
                label="Receptor"
                internal
                class="invoice-column-receiver"
              >
                {{ props.row.cfdi.receiverName }}
              </BTableColumn>
              <BTableColumn
                field="fecha"
                label="Fecha"
                internal
              >
                {{ props.row.cfdi.voucherDate | toHumanDate }}
              </BTableColumn>
            </template>
            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <BIcon
                      icon="emoticon-sad"
                      size="is-large"
                    />
                  </p>
                  <p>No se encontraron facturas de este cliente</p>
                </div>
              </section>
            </template>
          </BTable>
        </div>
      </section>

      <footer class="modal-card-foot">
        <button
          class="button"
          type="button"
          @click="closeModal"
        >
          Cerrar
        </button>
        <button
          :disabled="!checkedRows.length"
          class="button is-secondary"
          type="button"
          @click="addInvoices"
        >
          Agregar facturas
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import merge from 'lodash/merge';
import { mapState, mapMutations, mapActions } from 'vuex';
import api from '../../../lib/api/index';
import utils from '../../../lib/utils';
import CfdiRelationTypes from '../../../constants/CfdiRelationTypes';

export default {
  name: 'CfdisRelated',
  components: {
    AppPagination: () => import(/* webpackChunkName: "app-pagination" */ '@/components/AppPagination.vue'),
    AppSelectSearch: () => import(/* webpackChunkName: "app-select-search" */ '@/components/AppSelectSearch.vue'),
  },
  data() {
    return {
      buttonsMax: 5,
      loading: false,
      columns: [
        { field: 'folio', label: 'Folio' },
        { field: 'uuid', label: 'UUID' },
        { field: 'total', label: 'Monto' },
        { field: 'moneda', label: 'Moneda' },
        { field: 'receiverName', label: 'Receptor' },
        { field: 'fecha', label: 'Fecha' },
      ],
      checkedRows: [],
      properties: {
        replacementType: '',
        customer: 0,
      },
      invoices: [],
      totalPages: 0,
      total: 0,
      currentPage: 1,
      relationType: '04',
    };
  },
  computed: merge(
    mapState({
      companyId: ({ auth }) => auth.companyId,
      show: state => state.modalForm.show,
      props: state => state.modalForm.props,
      relationTypeCatalog: ({ ui }) => utils.mapCatalog(ui.catalogSat.relationType),
    }),
    {
      size() {
        return this.$route.query.size;
      },
      relationTypeSelected() {
        const type = this.getRelationTypes().find(
          relation => relation.value === this.properties.relationType,
        );

        return type;
      },
    },
  ),
  async beforeMount() {
    this.properties = { ...this.properties, ...this.props };

    if (!this.properties.relationType) {
      this.properties.relationType = this.getRelationTypes()[0].value;
    }

    await this.searchInvoices();
  },
  methods: merge(
    mapActions({
      requestCatalogSat: 'ui/requestCatalogSat',
    }),
    mapMutations({
      setValue: 'invoice/setValue',
      addCfdiRelated: 'invoice/addCfdiRelated',
      deleteUuidsRelated: 'invoice/deleteUuidsRelated',
    }),
    {
      async searchInvoices() {
        this.loading = true;

        const query = {
          'operation.customer': this.properties.customer,
          stamped: true,
          voucherType: 'I',
        };

        const {
          replacementType,
          relationType,
        } = this.properties;

        if (relationType === CfdiRelationTypes.ReplacementOfPreviousCfdis) {
          query.allDocuments = true;
        }

        if (replacementType === 'replacementCreditNote') {
          query.voucherType = 'E';

          if ([
            CfdiRelationTypes.CreditNoteOfRelatedDocuments,
            CfdiRelationTypes.ReturnOfProductsOnInvoices,
          ].includes(relationType)) {
            query.voucherType = 'I';
          } else if (relationType === CfdiRelationTypes.CfdiByApplicationOfAdvance) {
            query.voucherType = 'I,E';
          }
        } else if (replacementType === 'replacementRep') {
          query.voucherType = 'P';
        } else if (replacementType === 'replacementTradeTransfer') {
          query.voucherType = 'T';
        } else {
          query.voucherType = 'I';
        }

        const company = this.companyId;
        const size = 20;

        try {
          const payload = await api.invoice.getAll(
            company,
            'cfdiReplacements',
            this.currentPage,
            size,
            query,
          );

          this.invoices = payload.invoices;
          this.totalPages = payload.totalPages;
          this.total = payload.totalRows;
        } catch (error) {
          this.$danger({
            message: 'Ocurrió un error al obtener las facturas del cliente',
          });
        }

        this.loading = false;
      },
      async goToPage(page = 1) {
        this.currentPage = page;
        this.searchInvoices();
      },
      closeModal() {
        this.$modalForm({ show: false });
      },
      addInvoices() {
        this.checkedRows.forEach((invoice) => {
          this.deleteUuidsRelated({
            uuid: invoice.cfdi.complement.digitalTaxStamp.uuid,
          });

          this.addCfdiRelated({
            type: this.properties.relationType,
            uuid: invoice.cfdi.complement.digitalTaxStamp.uuid,
          });
        });

        this.$modalForm({ show: false });
      },
      getRelationTypes() {
        const { replacementTypesByDocument } = this.props;
        const relationTypeReplacement = replacementTypesByDocument[this.properties.replacementType];

        if (!relationTypeReplacement) {
          return [];
        }

        return this.relationTypeCatalog.filter(
          ({ value }) => relationTypeReplacement.includes(value),
        );
      },
      onChangeRelationType(value) {
        this.properties.relationType = value;
        this.searchInvoices();
      },
    },
  ),
};
</script>

<style scoped>
.invoice-column-folio {
  width: 175px;
}

.invoice-column-uuid {
  width: 380px;
}

.invoice-column-receiver {
  width: 250px;
}

.modal-card {
  width: auto;
}
</style>
